import { type ConversionMap, type AnalyticsEvents, type BaseAnalyticsEvent } from '../types/events'
import { DniAnalytics, type AnalyticsTransformer } from './Analytics'
import { type SetUserEvents } from './types'
import { addToSessionStorageEventQueue, clearSessionStorageEventQueue, getSessionStorageEventQueue, hasGtagScript, hasGtmScript, insertGtagScript, insertGtmScript, type TagServingPathConfig } from './utils'

type GtmContainerId = string | string[]
type MeasurementId = string | string[]

export type MeasurementIdConfig = Record<string, {
  include?: AnalyticsEvents['eventName'][]
}>

export interface GA4Config {
  measurementId?: MeasurementId
  measurementIdConfig?: MeasurementIdConfig
  gtmContainerId?: GtmContainerId
  gtmServingPathConfig?: TagServingPathConfig
  gtagServingPathConfig?: TagServingPathConfig
  adsConversionId?: string
  userId?: string | null
  conversionMap?: ConversionMap
  setUserEvents?: SetUserEvents
}

export class GA4 extends DniAnalytics<AnalyticsEvents> {
  readonly config: GA4Config

  private static instance: GA4 | null = null

  constructor(config: GA4Config) {
    super()
    this.config = config
    this.initialize()
  }

  // Static getInstance method for singleton pattern
  public static getInstance(config: GA4Config): GA4 {
    if (!GA4.instance) {
      GA4.instance = new GA4(config)
    }
    return GA4.instance
  }

  private initDataLayer() {
    window.dataLayer = window.dataLayer || []
    // https://developers.google.com/tag-platform/devguides/datalayer

    function gtag() {
      window.dataLayer?.push(arguments)
    }
    window.gtag = gtag
  }

  private getAllGtmContainerIds() {
    return Array.isArray(this.config.gtmContainerId) ? this.config.gtmContainerId : [this.config.gtmContainerId]
  }

  private getAllMeasurementIds() {
    if (!this.config.measurementId) {
      return []
    }
    return Array.isArray(this.config.measurementId) ? this.config.measurementId : [this.config.measurementId]
  }

  private getPrimaryMeasurementId() {
    return this.getAllMeasurementIds()[0]
  }

  private getMeasurementIdConfig() {
    return this.config.measurementIdConfig || {}
  }

  private getSendToIds(eventName: string) {
    const defaultSendToIds = [this.getPrimaryMeasurementId(), this.config.adsConversionId].filter(id => id !== undefined)
    const extraSendToIds = Object.entries(this.getMeasurementIdConfig()).map(([id, config]) => {
      if (config.include?.includes(eventName as AnalyticsEvents['eventName'])) {
        return id
      }
      return null
    }).filter(id => id !== null)
    return [...defaultSendToIds, ...extraSendToIds]
  }

  private configDataLayer() {
    this.getAllMeasurementIds().forEach((measurementId) => {
      window.gtag?.('config', measurementId, {
        user_id: this.config.userId,
      })
    })
    if (this.config.adsConversionId) {
      window.gtag?.('config', this.config.adsConversionId, {
        user_id: this.config.userId,
        allow_enhanced_conversions: true,
      })
    }
  }

  private initWithGtm() {
    this.initDataLayer()
    window.gtag?.('gtm.start', new Date().getTime(), 'gtm.js')
    window.gtag?.('js', new Date())
    this.configDataLayer()
    this.getAllGtmContainerIds().forEach((containerId) => {
      if (!containerId) {
        console.error('[GA4] gtmContainerId is undefined')
        return
      }
      const servingPath = this.config.gtmServingPathConfig?.[containerId]
      if (!hasGtmScript(containerId, servingPath)) {
        insertGtmScript(containerId, servingPath)
        return
      }
      /* 已被別的注入者用官方網址載入時不能再插一份，但這頁也就不走 gateway —— 靜默失效要留下訊號 */
      if (servingPath && hasGtmScript(containerId)) {
        console.warn(`[GA4] ${containerId} 已由 googletagmanager.com 載入，略過 gateway 插入，此頁不會走 ${servingPath}`)
      }
    })
    this.insertGtagScripts()
  }

  private insertGtagScripts() {
    this.getAllMeasurementIds().forEach((measurementId) => {
      const servingPath = this.config.gtagServingPathConfig?.[measurementId]
      if (!hasGtagScript(measurementId, servingPath)) {
        insertGtagScript(measurementId, servingPath)
      }
    })
  }

  private initWithoutGtm() {
    this.initDataLayer()
    window.gtag?.('js', new Date())
    this.configDataLayer()
    this.insertGtagScripts()
  }

  private processSessionStorageEventQueue() {
    getSessionStorageEventQueue().forEach(
      event => this.processAnalyticsEvent(event),
    )
    clearSessionStorageEventQueue()
  }

  private async afterInitialized() {
    if (this.config.setUserEvents) {
      try {
        await this.config.setUserEvents()
      }
      catch (error) {
        console.error('[GA4] afterInitialized setUserEvents', error)
      }
    }
    this.processSessionStorageEventQueue()
    /**
     * 所有所需事件都已經進到 queue，讓 GA4 可以開始發送事件
     */
    this.setIsReady(true)
  }

  /**
   * Only init once and if has gtmContainerId, ga4 should handle by gtm directly.
   */
  protected initialize() {
    if (!this.config.measurementId) {
      console.error('[GA4] measurementId is not defined')
      return
    }
    if (this.config.gtmContainerId) {
      this.initWithGtm()
    }
    else {
      this.initWithoutGtm()
    }
    this.afterInitialized()
  }

  private getUserProperties() {
    return new Promise<Record<string, string> | undefined>((resolve, reject) => {
      if (!window.gtag) {
        reject(new Error('[GA4] window.gtag is not defined'))
        return
      }
      window.gtag('get', this.getPrimaryMeasurementId(), 'user_properties', (value: Record<string, string>) => {
        resolve(value)
      })
    })
  }

  protected async sendAnalyticsEvent(customEvent: BaseAnalyticsEvent) {
    if (!window.gtag) {
      console.error('[GA4] window.gtag is not defined')
      return
    }

    try {
      const { eventType = 'event', eventName, eventParams, fieldName, callback } = customEvent

      switch (eventType) {
        case 'get':
          if (fieldName && callback) {
            window.gtag('get', this.getPrimaryMeasurementId(), fieldName, callback)
            break
          }
          throw new Error('[GA4] sendEvent: fieldName and callback are required for get event')
        case 'set':
          window.gtag(eventType, eventName, eventParams)
          break
        case 'event':
          const userProperties = await this.getUserProperties()
          window.gtag(eventType, eventName, {
            send_to: this.getSendToIds(eventName),
            ...eventParams,
            ...userProperties,
          })
          break
        default:
          throw new Error(`[GA4] sendEvent: unknown event type ${eventType}`)
      }
    }
    catch (error) {
      console.error('[GA4] sendEvent', error)
    }
  }

  public sendAnalyticsPendingEvent(customEvent: AnalyticsEvents, transformer?: AnalyticsTransformer) {
    const transformedEvent = transformer ? transformer(customEvent) : customEvent
    if (transformedEvent !== null) {
      addToSessionStorageEventQueue(transformedEvent)
    }
  }

  public getGtagValue<T>(key: string): Promise<T> {
    return new Promise<T>((resolve) => {
      this.processAnalyticsEvent({
        eventType: 'get',
        fieldName: key,
        callback: (value) => {
          resolve(value as T)
        },
      } as AnalyticsEvents)
    })
  }
}
